import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Plus, Minus } from "lucide-react";
import { treatments } from "@/data/treatments";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [treatOpen, setTreatOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setTreatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const navItems = [
    { label: "Home", to: "/" },
    { label: "About Natalia", to: "/about" },
    { label: "Treatments", to: "/treatments", hasDropdown: true },
    { label: "FAQ", to: "/faq" },
    { label: "Policies", to: "/policies" }
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled || menuOpen ? "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-[#E5E0DA]/60" : "bg-transparent"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 h-20 md:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2 group">
            <span className="text-xl md:text-[1.35rem] font-medium tracking-[-0.02em] text-[#1A1A1A]">Natalia</span>
            <span className="hidden sm:inline text-[0.72rem] text-[#8A847C] font-normal tracking-tight">Medical Aesthetics</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-[0.9rem] font-medium tracking-normal transition-opacity duration-300 hover:opacity-60 text-[#1A1A1A]`}
                >
                  {item.label}
                </Link>
                {item.hasDropdown && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="bg-[#FAF8F5] border border-[#E5E0DA] rounded-[24px] p-8 min-w-[280px] shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
                      <div className="overline mb-5">Signature Treatments</div>
                      <ul className="space-y-2.5">
                        {treatments.map((t) => (
                          <li key={t.slug}>
                            <Link
                              to={`/treatments/${t.slug}`}
                              data-testid={`nav-dropdown-${t.slug}`}
                              className="text-sm text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors block"
                            >
                              {t.name}
                            </Link>
                          </li>
                        ))}
                        <li className="pt-3 mt-3 border-t border-[#E5E0DA]">
                          <Link to="/treatments" className="text-sm font-medium text-[#1A1A1A]">
                            View All Treatments →
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA + hamburger */}
          <div className="flex items-center gap-4">
            <a
              href="tel:+13608204854"
              data-testid="header-book-now"
              className="hidden md:inline-flex btn-primary !py-2.5 !px-5 !text-[0.85rem]"
            >
              Book Now
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden flex items-center justify-center w-11 h-11"
              aria-label="Open menu"
            >
              <Menu strokeWidth={1.25} className="w-6 h-6 text-[#1A1A1A]" />
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen mobile menu */}
      <div
        data-testid="mobile-fullscreen-menu"
        className={`fixed inset-0 z-50 bg-[#FAF8F5] transition-all duration-500 ${menuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
      >
        <div className="h-20 md:h-24 flex items-center justify-between px-6 md:px-10 border-b border-[#E5E0DA]/60">
          <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-baseline gap-2">
            <span className="text-xl font-medium tracking-[-0.02em]">Natalia</span>
            <span className="hidden sm:inline text-[0.72rem] text-[#8A847C]">Medical Aesthetics</span>
          </Link>
          <button
            data-testid="mobile-menu-close"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-center w-11 h-11"
            aria-label="Close menu"
          >
            <X strokeWidth={1.25} className="w-6 h-6 text-[#1A1A1A]" />
          </button>
        </div>

        <div className="h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)] overflow-y-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-2xl">
            {navItems.map((item) =>
              item.hasDropdown ? (
                <div key={item.label} className="border-b border-[#E5E0DA]/60">
                  <button
                    data-testid="menu-treatments-toggle"
                    onClick={() => setTreatOpen(!treatOpen)}
                    className="w-full flex items-center justify-between py-2"
                  >
                    <span className="menu-link">Treatments</span>
                    {treatOpen ? <Minus strokeWidth={1.25} className="w-5 h-5" /> : <Plus strokeWidth={1.25} className="w-5 h-5" />}
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ${treatOpen ? "max-h-[600px] opacity-100 pb-6" : "max-h-0 opacity-0"}`}
                  >
                    <div className="pl-1 space-y-1">
                      {treatments.map((t) => (
                        <Link
                          key={t.slug}
                          to={`/treatments/${t.slug}`}
                          data-testid={`menu-treatment-${t.slug}`}
                          onClick={() => setMenuOpen(false)}
                          className="sub-link"
                        >
                          {t.name}
                        </Link>
                      ))}
                      <Link
                        to="/treatments"
                        onClick={() => setMenuOpen(false)}
                        className="sub-link font-medium !text-[#1A1A1A] pt-2"
                      >
                        View All Treatments →
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`menu-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setMenuOpen(false)}
                  className="menu-link border-b border-[#E5E0DA]/60"
                >
                  {item.label}
                </Link>
              )
            )}
            <Link
              to="/book"
              data-testid="menu-book-now"
              onClick={() => setMenuOpen(false)}
              className="menu-link border-b border-[#E5E0DA]/60"
            >
              Book Consultation
            </Link>
            <a
              href="tel:+13608204854"
              data-testid="menu-book-now-tel"
              className="menu-link border-b border-[#E5E0DA]/60"
            >
              Book Now
            </a>

            <div className="mt-14">
              <div className="overline mb-3">Visit</div>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">
                12040 98th Ave NE, Unit 200<br />
                Kirkland, WA 98034
              </p>
              <div className="overline mt-8 mb-3">Contact</div>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">
                <a href="tel:+13608204854" className="hover:text-[#1A1A1A]">(360) 820-4854</a><br />
                <a href="https://www.instagram.com/ne_cosmetologist" target="_blank" rel="noreferrer" className="hover:text-[#1A1A1A]">@ne_cosmetologist</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
